import {
    GetClusterProjectRowsQuery,
    useGetClusterProjectRowsQuery
} from "generated/graphql";
import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    ClusterProjectRow,
    getClusterProjectDisplayName
} from "in_queue/types/clusterProjectRowType";
import { Cluster } from "in_queue/types/clusterType";
import { sortBy } from "lodash";
import { useMemo } from "react";
import { Loading } from "types/loadingType";

export interface ClusterProjectsBaseScenario {
    title: string;
    runCompletedAt: Date | undefined;
}

export interface ClusterProjectRowsResult {
    rows: Loading<ClusterProjectRow[]>;
    baseScenario: ClusterProjectsBaseScenario | undefined;
    /** Whether the cluster spans study groups, so a group column earns its place. */
    hasMultipleStudyGroups: boolean;
}

type HasuraClusterProject =
    GetClusterProjectRowsQuery["cluster_projects"][number];

// The query filters on `cluster_id`, so every row belongs to the cluster
// passed in. `studyGroup` carries the one part that varies per project.
const toClusterProjectRow = (
    project: HasuraClusterProject,
    cluster: Cluster
): ClusterProjectRow | undefined => {
    const projectId = project.project_id;
    if (!projectId) {
        return undefined;
    }
    const trackedProject = project.tracked_projects[0];
    const { rank, percentile, total_ranked: totalRanked } = project;
    return {
        projectId,
        cluster,
        studyGroup: project.study_group ?? undefined,
        fuelType: project.fuel_type ?? "",
        size: {
            erisMw: project.eris_mw ?? 0,
            nrisMw: project.nris_mw ?? 0
        },
        isTracked: trackedProject !== undefined,
        isFavorited: project.project_configurations[0]?.is_favorited ?? false,
        projectName: trackedProject?.project_name ?? undefined,
        costPerMw: project.cost_per_mw ?? undefined,
        sumAllocatedCost: project.sum_allocated_cost ?? undefined,
        numAssignedUpgrades: project.num_assigned_upgrades ?? undefined,
        rankStats:
            rank == null || percentile == null || totalRanked == null
                ? undefined
                : { rank, percentile, totalRanked }
    };
};

/** Favorites first, then tracked projects, then by display name. */
export const sortClusterProjectRows = (
    rows: ReadonlyArray<ClusterProjectRow>
): ClusterProjectRow[] =>
    sortBy(rows, [
        (row) => !row.isFavorited,
        (row) => !row.isTracked,
        getClusterProjectDisplayName
    ]);

/**
 * Read one {@link ClusterProjectRow} per project in the cluster.
 *
 * Rows come back favorites first, then tracked, then by display name. The
 * grid applies no default sort, so that is the order the table opens on until
 * the user sorts a column. The table keeps rows in place on later updates, so
 * favoriting a row does not move it until the next visit.
 */
export const useClusterProjectRows = (): ClusterProjectRowsResult => {
    const cluster = useCluster();
    const { data, error } = useGetClusterProjectRowsQuery({
        variables: { clusterId: cluster.id },
        // Tracked names and membership come from the view, so an edit made on
        // a project page has to reach this list on the way back to it.
        fetchPolicy: "cache-and-network"
    });

    const rows = useMemo((): Loading<ClusterProjectRow[]> => {
        if (error) {
            console.error("Failed to load cluster projects", error);
            return [];
        }
        if (!data) {
            return "loading";
        }
        const rows = data.cluster_projects
            .map((project) => toClusterProjectRow(project, cluster))
            .filter((row): row is ClusterProjectRow => row !== undefined);
        return sortClusterProjectRows(rows);
    }, [data, error, cluster]);

    // Measured across every row, not the filtered set, so narrowing the table
    // to one group does not make the column disappear mid-interaction.
    const hasMultipleStudyGroups = useMemo(() => {
        if (rows === "loading") {
            return false;
        }
        const groups = new Set(
            rows.map((row) => row.studyGroup).filter(Boolean)
        );
        return groups.size > 1;
    }, [rows]);

    const base = data?.current_base_scenarios[0];

    return {
        rows,
        hasMultipleStudyGroups,
        baseScenario: base
            ? {
                  title: base.title ?? "",
                  runCompletedAt: base.run_completed_at
                      ? new Date(base.run_completed_at)
                      : undefined
              }
            : undefined
    };
};
